(() => {
  const componentName = 'pd-desktop-products';

  class PdDesktopProducts extends HTMLElement {
    constructor() {
      super();
      this._onFilterScroll = this._onFilterScroll.bind(this);
      this._onFilterResize = this._onFilterResize.bind(this);
      this._onFilterScrollButtonClick = this._onFilterScrollButtonClick.bind(this);
    }

    connectedCallback() {
      this.disconnectedCallback();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this._cacheElements();
      this._setupFilterScrolling();

      this.addEventListener('click', this._onFilterClick.bind(this), { signal: this.signal });
      document.addEventListener('click', this._onGearLinkClick.bind(this), { signal: this.signal });
      window.addEventListener('popstate', this._onHistoryChange.bind(this), { signal: this.signal });
      document.addEventListener('shopify:section:load', this._onSectionLoad.bind(this), { signal: this.signal });

      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', this._onSortChange.bind(this), { signal: this.signal });
      }

      this._sortByDisplayedPrice();
      this._applyFromLocation();
    }

    disconnectedCallback() {
      this._teardownFilterScrolling();

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = null;
      this.signal = null;
    }

    _cacheElements() {
      this.isGearFilterable = this.dataset.pdDesktopFilterable === 'true';
      this.filterButtons = Array.from(this.querySelectorAll('[data-gear-filter]'));
      this.productCards = Array.from(this.querySelectorAll('[data-pd-desktop-product]'));
      this.liveCount = this.querySelector('[data-pd-desktop-live-count]');
      this.liveCountValue = this.querySelector('[data-pd-desktop-live-count-value]');
      this.emptyState = this.querySelector('[data-pd-desktop-empty]');
      this.sortSelect = this.querySelector('[data-pd-desktop-sort]');
      this.filterShell = this.querySelector('[data-pd-desktop-filter-shell]');
      this.filterScroller = this.querySelector('[data-pd-desktop-filters]');
      this.filterScrollPrevious = this.querySelector('[data-pd-desktop-filter-scroll="previous"]');
      this.filterScrollNext = this.querySelector('[data-pd-desktop-filter-scroll="next"]');
    }

    _setupFilterScrolling() {
      this._teardownFilterScrolling();

      if (!this.filterScroller || !this.filterScrollPrevious || !this.filterScrollNext) {
        return;
      }

      this.filterScroller.addEventListener('scroll', this._onFilterScroll, {
        passive: true,
        signal: this.signal
      });

      this.filterScrollPrevious.addEventListener('click', this._onFilterScrollButtonClick, {
        signal: this.signal
      });
      this.filterScrollNext.addEventListener('click', this._onFilterScrollButtonClick, {
        signal: this.signal
      });

      if ('ResizeObserver' in window) {
        this.filterResizeObserver = new ResizeObserver(this._onFilterResize);
        this.filterResizeObserver.observe(this.filterScroller);
        this.filterButtons.forEach((button) => this.filterResizeObserver.observe(button));
      } else {
        window.addEventListener('resize', this._onFilterResize, { signal: this.signal });
      }

      this._updateFilterScrollState();
    }

    _teardownFilterScrolling() {
      if (this.filterResizeObserver) {
        this.filterResizeObserver.disconnect();
        this.filterResizeObserver = null;
      }

      if (this.filterScroller) {
        this.filterScroller.removeEventListener('scroll', this._onFilterScroll);
      }

      window.removeEventListener('resize', this._onFilterResize);

      if (this.filterScrollPrevious) {
        this.filterScrollPrevious.removeEventListener('click', this._onFilterScrollButtonClick);
      }

      if (this.filterScrollNext) {
        this.filterScrollNext.removeEventListener('click', this._onFilterScrollButtonClick);
      }
    }

    _onFilterScroll() {
      this._updateFilterScrollState();
    }

    _onFilterResize() {
      this._updateFilterScrollState();
      this._scrollSelectedFilterIntoView(this.filterButtons.find((button) => button.getAttribute('aria-pressed') === 'true'), 'auto');
    }

    _updateFilterScrollState() {
      if (!this.filterScroller || !this.filterScrollPrevious || !this.filterScrollNext) {
        return;
      }

      // Compare with the space available without arrows so they disappear as soon as the chips fit.
      const availableWidth = this.filterShell?.clientWidth || this.filterScroller.clientWidth;
      const isOverflowing = this.filterScroller.scrollWidth > availableWidth + 1;
      this.filterScrollPrevious.hidden = !isOverflowing;
      this.filterScrollNext.hidden = !isOverflowing;

      if (!isOverflowing) {
        this.filterScrollPrevious.disabled = true;
        this.filterScrollNext.disabled = true;
        this.filterShell?.removeAttribute('data-filter-overflowing');
        return;
      }

      this.filterShell?.setAttribute('data-filter-overflowing', '');
      const scrollerRect = this.filterScroller.getBoundingClientRect();
      const firstButtonRect = this.filterButtons[0]?.getBoundingClientRect();
      const lastButtonRect = this.filterButtons[this.filterButtons.length - 1]?.getBoundingClientRect();

      this.filterScrollPrevious.disabled = !firstButtonRect || firstButtonRect.left >= scrollerRect.left - 1;
      this.filterScrollNext.disabled = !lastButtonRect || lastButtonRect.right <= scrollerRect.right + 1;
    }

    _getFilterScrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    _scrollFilters(direction) {
      if (!this.filterScroller) {
        return;
      }

      const amount = Math.max(this.filterScroller.clientWidth * 0.75, 160);
      const left = direction === 'previous' ? -amount : amount;

      if (typeof this.filterScroller.scrollBy === 'function') {
        this.filterScroller.scrollBy({ left, behavior: this._getFilterScrollBehavior() });
      } else {
        this.filterScroller.scrollLeft += left;
      }
    }

    _onFilterScrollButtonClick(event) {
      const button = event.currentTarget;

      if (button.disabled) {
        return;
      }

      this._scrollFilters(button.dataset.pdDesktopFilterScroll);
    }

    _scrollSelectedFilterIntoView(button, behavior = this._getFilterScrollBehavior()) {
      if (!this.filterScroller || !button || this.filterScroller.scrollWidth <= this.filterScroller.clientWidth + 1) {
        return;
      }

      const scrollerRect = this.filterScroller.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      let left = 0;

      if (buttonRect.left < scrollerRect.left) {
        left = buttonRect.left - scrollerRect.left;
      } else if (buttonRect.right > scrollerRect.right) {
        left = buttonRect.right - scrollerRect.right;
      }

      if (!left) {
        return;
      }

      if (typeof this.filterScroller.scrollBy === 'function') {
        this.filterScroller.scrollBy({ left, behavior });
      } else {
        this.filterScroller.scrollLeft += left;
      }
    }

    _sortByDisplayedPrice() {
      const sort = this.sortSelect?.value;
      if (!['price-ascending', 'price-descending'].includes(sort)) return;
      const grid = this.querySelector('[data-pd-desktop-grid]');
      if (!grid) return;
      const direction = sort === 'price-ascending' ? 1 : -1;
      this.productCards.sort((a, b) => direction * (Number(a.dataset.productPrice) - Number(b.dataset.productPrice)));
      this.productCards.forEach((card) => grid.append(card));
    }

    _normalizeGear(value) {
      if (value === 'light') {
        return 'light-sound';
      }

      return ['all', 'hubs', 'displays', 'light-sound', 'accessories', 'other'].includes(value) ? value : 'all';
    }

    _applyFromLocation() {
      const url = new URL(window.location.href);
      const gear = this.isGearFilterable ? this._normalizeGear(url.searchParams.get('gear')) : 'all';
      this._applyFilter(gear);
    }

    _applyFilter(gear) {
      let visibleCount = 0;

      this.productCards.forEach((card) => {
        const visible = gear === 'all' || card.dataset.gearCategory === gear;
        card.hidden = !visible;

        if (visible) {
          visibleCount += 1;
        }
      });

      this.filterButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.gearFilter === gear));
      });

      this._scrollSelectedFilterIntoView(this.filterButtons.find((button) => button.dataset.gearFilter === gear));

      if (this.liveCountValue) {
        this.liveCountValue.textContent = String(visibleCount);
      } else if (this.liveCount) {
        this.liveCount.textContent = `${visibleCount} ${this.liveCount.dataset.countLabel || 'products'}`;
      }

      if (this.emptyState) {
        this.emptyState.hidden = visibleCount !== 0;
      }
    }

    _setGear(gear, { push = true, scroll = false } = {}) {
      if (!this.isGearFilterable) {
        return;
      }

      const normalizedGear = this._normalizeGear(gear);
      const url = new URL(window.location.href);

      if (normalizedGear === 'all') {
        url.searchParams.delete('gear');
      } else {
        url.searchParams.set('gear', normalizedGear);
      }

      url.searchParams.delete('page');
      url.hash = 'desktop-products';

      if (push) {
        window.history.pushState({}, '', url);
      }

      this._applyFilter(normalizedGear);

      if (scroll) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.scrollIntoView({ block: 'start', behavior });
      }
    }

    _onFilterClick(event) {
      const button = event.target.closest('[data-gear-filter]');

      if (!button || !this.contains(button)) {
        return;
      }

      this._setGear(button.dataset.gearFilter);
    }

    _onGearLinkClick(event) {
      if (!this.isGearFilterable) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest('a');

      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) {
        return;
      }

      const url = new URL(link.href, window.location.href);
      const gear = link.dataset.gear || url.searchParams.get('gear');
      const isDesktopGearAnchor = link.dataset.gear !== undefined || url.hash === '#desktop-products';

      if (
        !gear ||
        !isDesktopGearAnchor ||
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname
      ) {
        return;
      }

      event.preventDefault();
      this._setGear(gear, { scroll: true });
    }

    _onHistoryChange() {
      this._applyFromLocation();
    }

    _onSectionLoad(event) {
      const section = event.target;

      if (!(section === this.closest('.shopify-section') || section?.contains?.(this))) {
        return;
      }

      this._teardownFilterScrolling();
      this._cacheElements();
      this._setupFilterScrolling();
      this._applyFromLocation();
    }

    _onSortChange() {
      if (!this.sortSelect) {
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.sortSelect.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    }
  }

  if (!window.customElements.get(componentName)) {
    window.customElements.define(componentName, PdDesktopProducts);
  }
})();
